import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLeads = () => {
  const { getAuthConfig } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredLeads(leads);
    } else {
      setFilteredLeads(leads.filter(lead => lead.status === statusFilter));
    }
  }, [statusFilter, leads]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/leads`,
        getAuthConfig()
      );
      setLeads(response.data);
      setFilteredLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus, notes) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/leads/${leadId}`,
        { status: newStatus, notes },
        getAuthConfig()
      );
      toast.success('Lead mis à jour avec succès');
      await fetchLeads();
      setSelectedLead(null);
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'nouveau':
        return 'bg-orange-100 text-orange-700';
      case 'en_cours':
        return 'bg-blue-100 text-blue-700';
      case 'contacté':
        return 'bg-green-100 text-green-700';
      case 'converti':
        return 'bg-emerald-100 text-emerald-700';
      case 'perdu':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#336DBF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      {/* Filters */}
      <Card className="border-slate-200/70">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-slate-600" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les leads</SelectItem>
                <SelectItem value="nouveau">Nouveau</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="contacté">Contacté</SelectItem>
                <SelectItem value="converti">Converti</SelectItem>
                <SelectItem value="perdu">Perdu</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-600">
              {filteredLeads.length} lead{filteredLeads.length > 1 ? 's' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="border-slate-200/70">
        <CardHeader>
          <CardTitle>Liste des Leads</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLeads.length === 0 ? (
            <p className="text-slate-600 text-center py-8">Aucun lead à afficher</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Nom</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Email</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Projet</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Location</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Budget</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Statut</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Date</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-sm font-medium text-slate-900">{lead.name}</td>
                      <td className="p-4 text-sm text-slate-600">{lead.email}</td>
                      <td className="p-4 text-sm text-slate-600">{lead.project_type}</td>
                      <td className="p-4 text-sm text-slate-600">{lead.location}</td>
                      <td className="p-4 text-sm text-slate-600">{lead.budget_estimate || '-'}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-4">
                        <Button
                          onClick={() => setSelectedLead(lead)}
                          variant="outline"
                          size="sm"
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Voir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle>Détails du Lead</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Lead Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Nom</label>
                    <p className="text-slate-900 mt-1">{selectedLead.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Email</label>
                    <p className="text-slate-900 mt-1">{selectedLead.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Téléphone</label>
                    <p className="text-slate-900 mt-1">{selectedLead.phone || 'Non fourni'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Localisation</label>
                    <p className="text-slate-900 mt-1">{selectedLead.location}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Type de projet</label>
                    <p className="text-slate-900 mt-1">{selectedLead.project_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Surface</label>
                    <p className="text-slate-900 mt-1">{selectedLead.surface || '-'} m²</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Budget</label>
                    <p className="text-slate-900 mt-1">{selectedLead.budget_estimate || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Délai</label>
                    <p className="text-slate-900 mt-1">{selectedLead.desired_deadline || '-'}</p>
                  </div>
                </div>

                {selectedLead.message && (
                  <div>
                    <label className="text-sm font-medium text-slate-700">Message</label>
                    <p className="text-slate-900 mt-1 bg-slate-50 p-4 rounded">{selectedLead.message}</p>
                  </div>
                )}

                {/* Update Status */}
                <div className="border-t pt-6 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Changer le statut</label>
                    <Select
                      defaultValue={selectedLead.status}
                      onValueChange={(value) => {
                        setSelectedLead({ ...selectedLead, status: value });
                      }}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nouveau">Nouveau</SelectItem>
                        <SelectItem value="en_cours">En cours</SelectItem>
                        <SelectItem value="contacté">Contacté</SelectItem>
                        <SelectItem value="converti">Converti</SelectItem>
                        <SelectItem value="perdu">Perdu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700">Notes</label>
                    <Textarea
                      defaultValue={selectedLead.notes || ''}
                      onChange={(e) => {
                        setSelectedLead({ ...selectedLead, notes: e.target.value });
                      }}
                      placeholder="Ajoutez des notes..."
                      className="mt-2"
                      rows={3}
                    />
                  </div>

                  <Button
                    onClick={() => updateLeadStatus(selectedLead.id, selectedLead.status, selectedLead.notes)}
                    disabled={updatingStatus}
                    className="w-full bg-[#336DBF] hover:bg-[#2F62AA] text-white"
                  >
                    {updatingStatus ? 'Mise à jour...' : 'Enregistrer les modifications'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeads;